import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { SecurityService } from '../services/security.service';
import {
  LaunchDateRange,
  LaunchStatus,
  ServiceLaunchRecord,
  ServiceLaunchReportFilters,
  ServiceLaunchReportService,
} from './service-launch-report.service';

@Component({
  selector: 'app-service-launch-report',
  templateUrl: './service-launch-report.component.html',
  styleUrls: ['./service-launch-report.component.scss'],
  standalone: false,
})
export class ServiceLaunchReportComponent implements OnInit {
  readonly displayedColumns = ['company', 'service', 'launchedAt', 'email', 'status'];
  readonly pageSizeOptions = [25, 50, 100];
  readonly dateRanges: { value: LaunchDateRange; label: string }[] = [
    { value: 'last_7_days', label: 'serviceLaunchReport.filters.last7Days' },
    { value: 'last_30_days', label: 'serviceLaunchReport.filters.last30Days' },
    { value: 'current_month', label: 'serviceLaunchReport.filters.currentMonth' },
    { value: 'current_year', label: 'serviceLaunchReport.filters.currentYear' },
    { value: 'custom', label: 'serviceLaunchReport.filters.custom' },
  ];
  readonly statuses: { value: LaunchStatus; label: string }[] = [
    { value: 'launched', label: 'serviceLaunchReport.status.launched' },
    { value: 'not-launched', label: 'serviceLaunchReport.status.notLaunched' },
  ];

  companies: any[] = [];
  companyControl = new FormControl<any>('');
  availableServices: any[] = [];
  serviceControl = new FormControl<any>({ value: '', disabled: true });
  records: ServiceLaunchRecord[] = [];
  totalRecords = 0;
  isSuperAdmin = false;
  isLoading = false;
  dateRangeError = false;

  filters: ServiceLaunchReportFilters = this.createDefaultFilters();

  constructor(
    private securityService: SecurityService,
    private reportService: ServiceLaunchReportService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.isSuperAdmin = this.securityService.isSuperAdmin();
    if (!this.isSuperAdmin) return;
    this.applyQueryParamsToState();

    this.reportService.getCompanies().subscribe({
      next: (companies) => {
        this.companies = companies;
        this.restorePartnerAndServiceFromQuery();
        this.loadPage();
      },
      error: (error) => {
        console.error('[SERVICE-LAUNCH-REPORT] Error cargando compañías:', error);
        this.loadPage();
      },
    });
  }

  onCompanyChange(serviceIdToRestore: string | null = null): void {
    this.availableServices = [];
    this.clearService();
    if (!this.filters.companyId) {
      this.availableServices = [];
      return;
    }

    const partnerId = this.filters.companyId;
    this.reportService.getServices(partnerId).subscribe({
      next: (services) => {
        if (this.filters.companyId !== partnerId) return;
        this.availableServices = services;
        this.serviceControl.enable({ emitEvent: false });
        if (serviceIdToRestore) {
          const selectedService = services.find((service) => String(service.id) === serviceIdToRestore);
          if (selectedService) this.selectService(selectedService);
        }
      },
      error: (error) => console.error('[SERVICE-LAUNCH-REPORT] Error cargando servicios:', error),
    });
  }

  onDateRangeChange(): void {
    if (this.filters.dateRange !== 'custom') {
      this.filters.startDate = null;
      this.filters.endDate = null;
    }
    this.dateRangeError = false;
  }

  get filteredCompanies(): any[] {
    const value = this.companyControl.value;
    const search = typeof value === 'string' ? value.trim().toLocaleLowerCase() : '';
    if (!search) return this.companies;

    return this.companies.filter((company) =>
      this.displayCompany(company).toLocaleLowerCase().includes(search)
    );
  }

  displayCompany = (company: any): string =>
    company?.name || company?.name_partner || company?.business_name || String(company?.id ?? '');

  onCompanyInput(): void {
    this.filters.companyId = null;
    this.onCompanyChange();
  }

  selectCompany(company: any): void {
    if (!company) {
      this.clearCompany();
      return;
    }
    this.companyControl.setValue(company);
    this.filters.companyId = String(company.id);
    this.onCompanyChange();
  }

  clearCompany(): void {
    this.companyControl.setValue('');
    this.filters.companyId = null;
    this.onCompanyChange();
  }

  get filteredServices(): any[] {
    const value = this.serviceControl.value;
    const search = typeof value === 'string' ? value.trim().toLocaleLowerCase() : '';
    if (!search) return this.availableServices;

    return this.availableServices.filter((service) =>
      this.displayService(service).toLocaleLowerCase().includes(search)
    );
  }

  displayService = (service: any): string =>
    service?.name_service || service?.name || service?.serviceName || String(service?.id ?? '');

  onServiceInput(): void {
    this.filters.serviceId = null;
  }

  selectService(service: any): void {
    if (!service) {
      this.clearService();
      return;
    }
    this.serviceControl.setValue(service);
    this.filters.serviceId = String(service.id);
  }

  clearService(): void {
    this.filters.serviceId = null;
    this.serviceControl.setValue('', { emitEvent: false });
    if (this.filters.companyId) {
      this.serviceControl.enable({ emitEvent: false });
    } else {
      this.serviceControl.disable({ emitEvent: false });
    }
  }

  search(): void {
    this.dateRangeError = !this.isValidDateRange();
    if (this.dateRangeError) return;

    this.filters.pageIndex = 0;
    this.syncUrlQueryParams();
    this.loadPage();
  }

  clearFilters(): void {
    this.filters = this.createDefaultFilters();
    this.companyControl.setValue('');
    this.availableServices = [];
    this.clearService();
    this.dateRangeError = false;
    this.syncUrlQueryParams();
    this.loadPage();
  }

  onPage(event: PageEvent): void {
    this.filters.pageIndex = event.pageIndex;
    this.filters.pageSize = event.pageSize;
    this.syncUrlQueryParams();
    this.loadPage();
  }

  private applyQueryParamsToState(): void {
    const query = this.activatedRoute.snapshot.queryParamMap;
    const dateRange = query.get('dateRange') as LaunchDateRange;
    const startDate = query.get('startDate');
    const endDate = query.get('endDate');
    const status = query.get('status') as LaunchStatus;
    const pageIndex = Number(query.get('pageIndex'));
    const pageSize = Number(query.get('pageSize'));

    if (['last_7_days', 'last_30_days', 'current_month', 'current_year', 'custom'].includes(dateRange as string)) {
      this.filters.dateRange = dateRange;
    }
    this.filters.startDate = this.parseDate(startDate);
    this.filters.endDate = this.parseDate(endDate);
    this.filters.companyId = query.get('partnerId');
    this.filters.serviceId = query.get('serviceId');
    if (status === 'launched' || status === 'not-launched') this.filters.status = status;
    if (Number.isInteger(pageIndex) && pageIndex >= 0) this.filters.pageIndex = pageIndex;
    if (this.pageSizeOptions.includes(pageSize)) this.filters.pageSize = pageSize;
  }

  private restorePartnerAndServiceFromQuery(): void {
    if (!this.filters.companyId) return;
    const partner = this.companies.find((company) => String(company.id) === this.filters.companyId);
    if (!partner) {
      this.filters.companyId = null;
      this.filters.serviceId = null;
      return;
    }

    const serviceId = this.filters.serviceId;
    this.companyControl.setValue(partner);
    this.onCompanyChange(serviceId);
    if (serviceId) this.filters.serviceId = serviceId;
  }

  private syncUrlQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        dateRange: this.filters.dateRange || null,
        startDate: this.filters.dateRange === 'custom' && this.filters.startDate ? this.formatDate(this.filters.startDate) : null,
        endDate: this.filters.dateRange === 'custom' && this.filters.endDate ? this.formatDate(this.filters.endDate) : null,
        partnerId: this.filters.companyId || null,
        serviceId: this.filters.serviceId || null,
        status: this.filters.status || null,
        pageIndex: this.filters.pageIndex || null,
        pageSize: this.filters.pageSize === 25 ? null : this.filters.pageSize,
      },
      replaceUrl: true,
    });
  }

  private parseDate(value: string | null): Date | null {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private loadPage(): void {
    if (!this.isSuperAdmin) return;

    this.isLoading = true;
    this.reportService.getPage(this.filters).subscribe({
      next: (page) => {
        this.records = page.data;
        this.totalRecords = page.total;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('[SERVICE-LAUNCH-REPORT] Error cargando altas de servicios:', error);
        this.isLoading = false;
      },
    });
  }

  private isValidDateRange(): boolean {
    if (this.filters.dateRange !== 'custom') return true;
    if (!this.filters.startDate || !this.filters.endDate) return false;
    return this.filters.startDate <= this.filters.endDate;
  }

  private createDefaultFilters(): ServiceLaunchReportFilters {
    return {
      dateRange: null,
      startDate: null,
      endDate: null,
      companyId: null,
      serviceId: null,
      status: null,
      pageIndex: 0,
      pageSize: 25,
    };
  }
}
